import React, {
  useEffect, useState, useContext,
} from 'react';
import {
  Card, Checkbox, FormControlLabel, Button, Radio,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import FallbackOnError from './FallbackOnError';
import Alert from '../utils/Alert';
import subscriptionService from '../services/SubscriptionService';
import { logReactErrBoundaryError } from '../utils/index';
import APIWrapper from '../utils/graphqlwrapper';
import AppContext from '../context';
import logger from '../utils/logger';

function SBSForm(props) {
  const {
    userEmail, setCurrentExecutionId, currentExecutionId, executionStatus,
  } = props;
  const { setShouldLogOut } = useContext(AppContext);
  const { client } = useParams();
  const [dateError, setDateError] = useState(null);
  const [alertMessage, setAlertMessage] = useState();
  const [formData, setFormData] = useState({
    includeExclusions: false,
    dateBreakdown: 'day',
    groupBy: 'sumByChannelCategory',
    separateRecurring: false,
    removeTotals: false,
    exportStyledExcelSheet: false,
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  });

  // Reset alert message once we get the execution status
  useEffect(() => {
    setAlertMessage();
  }, [executionStatus]);

  const startStepFunctionAndSubscribe = async () => {
    /**
     * starts the SBS step function and background subscription service
     */
    try {
      // Start the step function
      const res = await APIWrapper.queryApi(
        { query: 'sumBySourceStepFunction' },
        setShouldLogOut,
        {
          clientID: client,
          startDate: formData.startDate,
          endDate: formData.endDate,
          includeExclusions: formData.includeExclusions,
          separateRecurring: formData.separateRecurring,
          removeTotals: formData.removeTotals,
          exportStyledExcelSheet: formData.exportStyledExcelSheet,
          userEmail,
          aggType: formData.groupBy,
          breakdownPeriod: formData.dateBreakdown,
          downloadRequest: true,
        },
      );
      const executionId = JSON.parse(res.data[0]).id;
      setCurrentExecutionId(executionId);
      logger.info('SBS Step function started with execution ID: ', executionId);

      // Start the background subscription service
      await subscriptionService.startSumBySourceSubscription(executionId, client);

      logger.info('Background subscription started - will receive notifications even when navigating away');
      setAlertMessage({
        severity: 'success',
        message: 'Report Processing: You can navigate away from this page. You\'ll get a notification when the report is ready!',
      });
    } catch (err) {
      logger.error('SBS Step function error: ', err);
      setAlertMessage({
        severity: 'error',
        message: 'There was an error starting the report generation. If the error persists, contact tools-help.',
      });
    }
  };

  const onSubmit = async () => {
    /**
     * Validate that user has a date range selected, then
     * trigger the step function and background subscription service
     */
    if (dateError) {
      setAlertMessage({ severity: 'error', message: 'Please select a valid date range before submitting the form.' });
    } else {
      await startStepFunctionAndSubscribe();
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const handleRadioButtonChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateDateValues = (startDate, endDate) => {
    // validate date values
    // set error message if not valid
    setDateError(true);
    if (startDate < '2000-01-01' || endDate < '2000-01-01') {
      setAlertMessage({ message: 'Please select a date after 01-01-2000', severity: 'error' });
    } else if (startDate > dayjs().format('YYYY-MM-DD') || endDate > dayjs().format('YYYY-MM-DD')) {
      setAlertMessage({ message: 'You cannot select a date in the future', severity: 'error' });
    } else if (!dayjs(startDate).isValid() || !dayjs(endDate).isValid()) {
      setAlertMessage({ message: 'Invalid date format', severity: 'error' });
    } else {
      setAlertMessage();
      setDateError(false);
    }
  };

  // get date range picker value
  const handleDateRangeChange = (newValue) => {
    const dateRange = [];
    if (newValue.includes(null)) {
      setDateError(true);
      // check if dates are missing
      setAlertMessage({ message: 'Please select a date', severity: 'error' });
    } else {
      newValue.forEach((d) => {
        const date = new Date(d.$d);
        const formattedDate = dayjs(date).format('YYYY-MM-DD');
        dateRange.push(formattedDate);
      });
      // validate date range
      validateDateValues(dateRange[0], dateRange[1]);
      // set formData
      setFormData({ ...formData, startDate: dateRange[0], endDate: dateRange[1] });
    }
  };

  const {
    includeExclusions,
    dateBreakdown,
    groupBy,
    separateRecurring,
    removeTotals,
    exportStyledExcelSheet,
  } = formData;

  // define min and max dates for date picker
  const minStartDate = dayjs('2000-01-01T00:00:00.000');
  const maxEndDate = dayjs();
  // allows us to use quarterOfYear plugin
  dayjs.extend(quarterOfYear);

  // shortcut chips for date picker
  const shortcutsItems = [
    {
      label: 'This Week',
      getValue: () => {
        const today = dayjs();
        return [today.startOf('week'), today];
      },
    },
    {
      label: 'This Month',
      getValue: () => {
        const today = dayjs();
        return [today.startOf('month'), today];
      },
    },
    {
      label: 'This Quarter',
      getValue: () => {
        const today = dayjs();
        return [today.startOf('quarter'), today];
      },
    },
    {
      label: 'This Year',
      getValue: () => {
        const today = dayjs();
        return [today.startOf('year'), today];
      },
    },
    { label: 'Reset', getValue: () => [null, null] },
  ];

  return (
    <ErrorBoundary
      FallbackComponent={FallbackOnError}
      onError={logReactErrBoundaryError}
      onReset={(details) => {
        logger.info('details', details);
        // Reset the state of your app so the error doesn't happen again
      }}
    >
      <Card
        variant="outlined"
        sx={{
          p: 3, width: '70%', margin: 'auto',
        }}
      >
        <div id="alert-message">
          {alertMessage && <Alert alert={alertMessage} />}
        </div>
        <div className="sumBySourceWidget-container">
          <div className="sumBySourceWidget-column">
            <h4>Date range</h4>
            <div className="sumBySourceWidget-questions">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateRangePicker
                  localeText={{ start: 'Start date', end: 'End date' }}
                  disableFuture
                  minDate={minStartDate}
                  maxDate={maxEndDate}
                  format="MM-DD-YYYY"
                  calendars={1}
                  onAccept={(newValue) => handleDateRangeChange(newValue)}
                  onError={() => setDateError(true)}
                  defaultValue={[dayjs().startOf('month'), dayjs()]}
                  slotProps={{
                    shortcuts: {
                      items: shortcutsItems,
                    },
                  }}
                />
              </LocalizationProvider>
              <h4>Date breakdown</h4>
              <FormControlLabel
                control={(
                  <Radio
                    name="dateBreakdown"
                    value="day"
                    onChange={handleRadioButtonChange}
                    checked={dateBreakdown === 'day'}
                  />
                )}
                label="Breakdown by day"
              />
              <FormControlLabel
                control={(
                  <Radio
                    name="dateBreakdown"
                    value="month"
                    onChange={handleRadioButtonChange}
                    checked={dateBreakdown === 'month'}
                  />
                )}
                label="Breakdown by month"
              />
              <FormControlLabel
                control={(
                  <Radio
                    name="dateBreakdown"
                    value="quarter"
                    onChange={handleRadioButtonChange}
                    checked={dateBreakdown === 'quarter'}
                  />
                )}
                label="Breakdown by quarter"
              />
            </div>
          </div>
          <div className="sumBySourceWidget-column">
            <div className="sumBySourceWidget-questions">
              <h4>Group by</h4>
              <FormControlLabel
                control={(
                  <Radio
                    name="groupBy"
                    value="sumByChannelCategory"
                    onChange={handleRadioButtonChange}
                    checked={groupBy === 'sumByChannelCategory'}
                  />
                )}
                label="Channel Category"
              />
              <FormControlLabel
                control={(
                  <Radio
                    name="groupBy"
                    value="sumBySource"
                    onChange={handleRadioButtonChange}
                    checked={groupBy === 'sumBySource'}
                  />
                )}
                label="Source"
              />
              <h4>Additional Filters</h4>
              <FormControlLabel
                control={(
                  <Checkbox
                    name="includeExclusions"
                    onChange={handleCheckboxChange}
                    checked={includeExclusions}
                  />
                )}
                label="Include transactions flagged as exclusions"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    name="separateRecurring"
                    onChange={handleCheckboxChange}
                    checked={separateRecurring}
                  />
                )}
                label="Separate recurring"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    name="removeTotals"
                    onChange={handleCheckboxChange}
                    checked={removeTotals}
                  />
                )}
                label="Remove Totals"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    name="exportStyledExcelSheet"
                    onChange={handleCheckboxChange}
                    checked={exportStyledExcelSheet}
                  />
                )}
                label="Export as styled Excel sheet"
              />
            </div>
          </div>
        </div>
        <div className="sumBySource-button-container">
          <Button
            sx={{ width: '350px' }}
            variant="contained"
            onClick={onSubmit}
            disabled={
              currentExecutionId && subscriptionService.isSubscriptionActive(currentExecutionId)
            }
          >
            {currentExecutionId
              && subscriptionService.isSubscriptionActive(currentExecutionId)
              ? 'Report Generation In Progress...'
              : 'Submit'}
            {' '}

          </Button>
        </div>
      </Card>
    </ErrorBoundary>
  );
}

export default SBSForm;

SBSForm.propTypes = {
  userEmail: PropTypes.string,
  setCurrentExecutionId: PropTypes.func,
  currentExecutionId: PropTypes.string,
  executionStatus: PropTypes.string,
};
